import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';

import { useImageUpload } from '@valwin/facto/hooks';

import { uploadAsset } from '~entities/asset';
import type { SharedData } from '~features/integrations/types';
import { ShareModal } from '~features/integrations/ui';

import { useConnectFacebook } from '../model/useConnectFacebook';
import { useGetFacebookPages } from '../model/useGetFacebookPages';
import { usePublishFacebookPost } from '../model/usePublishFacebookPost';
import { FacebookShareFormProvider } from './context';
import { type FacebookShareFormValues, facebookShareSchema } from './types';
import { FacebookShareForm, FacebookSharePreview } from './ui';

export function useFacebookShare(pharmacyId: string, sharedData: SharedData) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const { pages } = useGetFacebookPages(pharmacyId);
  const { mutate: connect } = useConnectFacebook(pharmacyId);
  const publishMutation = usePublishFacebookPost();

  const { handleUpload } = useImageUpload(async (file: File) => {
    const { assetUrl } = await uploadAsset(pharmacyId, file);

    return assetUrl;
  });

  const form = useForm<FacebookShareFormValues>({
    resolver: zodResolver(facebookShareSchema),
    defaultValues: {
      pageId: '',
      message: sharedData.postMessage,
      photos: sharedData.imageUrl ? [sharedData.imageUrl] : [],
    },
  });

  const open = () => {
    if ((pages?.length ?? 0) > 0) {
      form.reset({
        pageId: pages![0].pageId,
        message: sharedData.postMessage,
        photos: sharedData.imageUrl ? [sharedData.imageUrl] : [],
      });
      setIsOpen(true);

      return;
    }

    connect(undefined, {
      onSuccess: (updatedPages) => {
        if (updatedPages.length > 0) {
          form.reset({
            pageId: updatedPages[0].pageId,
            message: sharedData.postMessage,
            photos: sharedData.imageUrl ? [sharedData.imageUrl] : [],
          });
          setIsOpen(true);
        }
      },
    });
  };

  const close = () => {
    setIsOpen(false);
  };

  const handleSubmit = form.handleSubmit((data) => {
    publishMutation.mutate({
      pharmacyId,
      pageId: data.pageId,
      params: {
        message: `${data.message}\n\n${sharedData.shareUrl}`,
        photos: data.photos,
      },
    });
  });

  const modal = (
    <FacebookShareFormProvider form={form}>
      <ShareModal
        open={isOpen}
        onClose={close}
        title={t('integrations facebook share title')}
        form={
          <FacebookShareForm
            pages={pages ?? []}
            shareUrl={sharedData.shareUrl}
            onImageUpload={handleUpload}
          />
        }
        preview={
          <FacebookSharePreview
            shareUrl={sharedData.shareUrl}
            pages={pages ?? []}
          />
        }
        onSubmit={handleSubmit}
        mutationStatus={publishMutation.status}
      />
    </FacebookShareFormProvider>
  );

  return { open, modal };
}
